import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLocale } from '@/hooks/useLocale'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Clock, Eye, Calendar } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import VideoCard from '@/components/videos/VideoCard'
import Card from '@/components/ui/Card'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { videosApi } from '@/services/videosApiService'
import { mockVideos } from '@/services/mockDataService'
import { getLocalizedValue } from '@sakeena/shared'

// Helper to normalize video data (handle both camelCase and PascalCase)
const normalizeVideo = (v: any): any => ({
  id: v.id || v.Id || '',
  title: v.title || v.Title || '',
  description: v.description || v.Description || '',
  url: v.url || v.Url || '',
  thumbnailUrl: v.thumbnailUrl || v.ThumbnailUrl || '',
  topic: v.topic || v.Topic || '',
  durationSeconds: v.durationSeconds ?? v.DurationSeconds ?? 0,
  views: v.views ?? v.Views ?? 0,
  isPinned: v.isPinned ?? v.IsPinned ?? false,
  createdAt: v.createdAt || v.CreatedAt || new Date().toISOString(),
  relatedVideos: (v.relatedVideos || v.RelatedVideos || []).map((rv: any) => ({
    id: rv.id || rv.Id || '',
    title: rv.title || rv.Title || '',
    thumbnailUrl: rv.thumbnailUrl || rv.ThumbnailUrl || '',
    views: rv.views ?? rv.Views ?? 0,
  })),
})

export default function VideoDetailPage() {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'ar'
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { localizePath } = useLocale()
  const [video, setVideo] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchVideo = async () => {
      if (!id) {
        setError('معرف الفيديو غير موجود')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const data = await videosApi.getVideo(id)
        if (data) {
          setVideo(normalizeVideo(data))
        }
      } catch (err: any) {
        console.error('Failed to fetch video:', err)
        
        // Check if it's a 404 (not found) error
        if (err?.response?.status === 404) {
          setError('الفيديو غير موجود')
        } else {
          // Extract error message
          let errorMessage = 'فشل تحميل بيانات الفيديو'
          if (err?.response?.data?.error) {
            const errorData = err.response.data.error
            if (typeof errorData === 'string') {
              errorMessage = errorData
            } else if (errorData?.message) {
              errorMessage = errorData.message
            }
          } else if (err?.message) {
            errorMessage = err.message
          }
          setError(errorMessage)
        }
        
        // Fallback to mock data only if not 404
        if (err?.response?.status !== 404) {
          const mockVideo = mockVideos.find((v) => v.id.toString() === id)
          if (mockVideo) {
            const durationParts = mockVideo.duration ? mockVideo.duration.split(':') : ['0', '0']
            setVideo(normalizeVideo({
              id: mockVideo.id.toString(),
              title: mockVideo.title,
              url: mockVideo.url || '',
              thumbnailUrl: mockVideo.thumbnail,
              topic: mockVideo.topic,
              durationSeconds: parseInt(durationParts[0]) * 60 + parseInt(durationParts[1]) || 0,
              views: mockVideo.views,
              isPinned: false,
              createdAt: new Date().toISOString(),
              description: mockVideo.description,
            }))
            setError(null) // Clear error if mock data found
          }
        }
      } finally {
        setIsLoading(false)
      }
    }
    fetchVideo()
  }, [id, currentLang])

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (!video && !isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb 
          items={[
            { label: t('common.home'), to: '/' },
            { label: t('common.videos'), to: '/videos' },
            { label: t('public.videos.notFound') || 'الفيديو غير موجود' },
          ]} 
          className="mb-6" 
        />
        <Card className="p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {error || t('public.videos.notFound') || 'الفيديو غير موجود'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error === 'الفيديو غير موجود' 
              ? 'عذراً، الفيديو الذي تبحث عنه غير موجود أو تم حذفه.'
              : 'حدث خطأ أثناء تحميل بيانات الفيديو. يرجى المحاولة مرة أخرى.'}
          </p>
          <button
            onClick={() => navigate(localizePath('/videos'))}
            className="btn-primary"
          >
            {t('public.videos.backToList') || 'العودة إلى الفيديوهات'}
          </button>
        </Card>
      </div>
    )
  }

  // Format duration from seconds
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Get related videos from API response or fallback
  const relatedVideos = video.relatedVideos || []

  const formatViews = (views: number) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)} مليون`
    }
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)} ألف`
    }
    return views.toString()
  }

  // Video URL - can be YouTube embed or direct video URL
  const getVideoEmbedUrl = (url: string) => {
    // Check if it's a YouTube URL
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const youtubeId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]
      return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : url
    }
    return url
  }

  const videoUrl = video.url ? getVideoEmbedUrl(video.url) : 'https://www.youtube.com/embed/dQw4w9WgXcQ'

  return (
    <>
      <Helmet>
        <title>{getLocalizedValue(video.title, currentLang)} - {t('common.videos')} - سكينة</title>
        <meta name="description" content={getLocalizedValue(video.description || video.title, currentLang)} />
        <meta property="og:title" content={getLocalizedValue(video.title, currentLang)} />
        <meta property="og:description" content={getLocalizedValue(video.description, currentLang)} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb 
          items={[
            { label: t('common.home'), to: '/' },
            { label: t('common.videos'), to: '/videos' },
            { label: getLocalizedValue(video.title, currentLang) },
          ]} 
          className="mb-6" 
        />

        {error && video && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-yellow-700 dark:text-yellow-300">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Video Player */}
            <Card className="p-0 mb-6 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
              <div className="aspect-video bg-black relative">
                {video.url?.includes('youtube') || video.url?.includes('youtu.be') ? (
                  <iframe
                    src={videoUrl}
                    title={getLocalizedValue(video.title, currentLang)}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    onError={(e) => {
                      console.error('Video iframe error:', e)
                    }}
                  />
                ) : (
                  <video 
                    controls 
                    className="w-full h-full" 
                    src={video.url}
                    onError={(e) => {
                      console.error('Video playback error:', e)
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </Card>

            {/* Video Info */}
            <Card className="p-6 mb-6 dark:bg-gray-800 dark:border-gray-700">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">{getLocalizedValue(video.title, currentLang)}</h1>
              <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  <span>{formatViews(video.views)} {t('public.videos.views')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{formatDuration(video.durationSeconds)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(video.createdAt).toLocaleDateString('ar-EG')}</span>
                </div>
              </div>
              {video.description && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('public.videos.description') || 'الوصف'}</h2>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed" dir="rtl">
                    {getLocalizedValue(video.description, currentLang)}
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar - Related Videos */}
          <div className="lg:col-span-1">
            <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('public.videos.related') || 'فيديوهات ذات صلة'}</h2>
              {relatedVideos.length > 0 ? (
                <div className="space-y-4">
                  {relatedVideos.map((relatedVideo: any) => (
                    <VideoCard key={relatedVideo.id} video={relatedVideo} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 text-sm">{t('public.videos.noRelated') || 'لا توجد فيديوهات ذات صلة'}</p>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
